import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormControl, FormGroup } from '@angular/forms';
import { OrganisationOption, UserForm } from '../models/forms/user-form';
import { UserFormService } from '../services/user-form.service';
import { OrganisationTree } from '../models/organisation-tree';
import { OrganisationsAdminService } from '../services/organisations-admin.service';
import { catchError, map, Observable, of, Subscription, switchMap, zip } from 'rxjs';
import { UsersAdminService } from '../services/users-admin.service';
import { LaunchpadUser } from '../models/launchpad-user';
import { Application } from '../models/application';
import { TranslateService } from '@ngx-translate/core';
import { GraphQLError } from 'graphql/error';

@Component({
    selector: 'app-new-user',
    templateUrl: './new-user.component.html',
    styleUrls: ['./new-user.component.sass'],
    standalone: false
})
export class NewUserComponent implements OnInit, OnDestroy {

  userForm!: FormGroup<UserForm>;

  @Input() organisationTree: OrganisationTree | null = null;

  private _selectedUser: LaunchpadUser | null = null

  @Input() set selectedUser(selectedUser: LaunchpadUser | null) {
    this._selectedUser = selectedUser;
    this.editMode = selectedUser != null;

    this.populateUserForm();
  };

  // Show the user details without the ability to change anything
  @Input() readOnly: boolean = false;

  editMode: boolean = false;
  submitting: boolean = false;

  get isTeamMember(): boolean {
    return this._selectedUser?.isTeamMember ?? false;
  }

  error: boolean = false;
  errorCode: string | null = null;

  get organisations(): FormControl<OrganisationOption[]> {
    return this.userForm.get('organisations') as FormControl;
  }

  get emailAddress(): FormControl {
    return this.userForm.get('emailAddress') as FormControl;
  }

  get firstName(): FormControl {
    return this.userForm.get('firstName') as FormControl;
  }

  get surname(): FormControl {
    return this.userForm.get('surname') as FormControl;
  }

  availableApplications: Application[] = [];

  orgSubscription: Subscription = new Subscription();
  appSubscription: Subscription = new Subscription();

  constructor(protected activeModal: NgbActiveModal,
              private userFormService: UserFormService,
              private orgAdminService: OrganisationsAdminService,
              private userAdminService: UsersAdminService,
              private translateService: TranslateService) {
    this.userForm = this.userFormService.createUserForm();
  }

  ngOnInit(): void {
    this.orgSubscription = this.organisations.valueChanges.pipe(
      map(organisations => organisations.map(o => o.organisationRef)),
      switchMap(orgRefs => this.getOrganisationApplications(orgRefs))
    ).subscribe(applications => {
      this.availableApplications = applications;
      this.userFormService.prepopulateApplicationsWithDefault(this.userForm.controls.applications, this.availableApplications);
      this.userFormService.refreshApplicationsForm(this.userForm.controls.applications, this.availableApplications);
    })
  }

  ngOnDestroy() {
    this.orgSubscription.unsubscribe();
    this.appSubscription.unsubscribe();
  }

  submitForm(): void {
    if (this.readOnly) {
      return;
    }

    this.submitting = true;
    if (this.editMode) {
      // Update an existing user
      this.userAdminService.updateUser(this.userForm.getRawValue()).subscribe(
        updatedUser => {
          this.activeModal.close(updatedUser);
          this.submitting = false;
        }, error => {
          this.submitting = false;
        })
    } else {
      // Create a new user
      this.userAdminService.createUser(this.userForm.value).subscribe(
        newUser => {
          this.activeModal.close(newUser);
          this.submitting = false;
        }, ({ networkError }) => {
          this.processError(networkError);
          this.userForm.markAsUntouched();

          this.submitting = false;
      })
    }

  }

  populateUserForm(): void {
    if (this._selectedUser == null) {
      this.userForm.reset();
      return;
    }

    this.userFormService.populateForm(this.userForm, this._selectedUser);

    this.appSubscription.unsubscribe();
    this.appSubscription = this.getOrganisationApplications(this._selectedUser.organisations.map(o => o.organisationRef))
      .subscribe(apps => {
        this.availableApplications = apps;
      });
  }

  // Get a merged list of distinct applications belonging to the organisations
  private getOrganisationApplications(organisationRefs: string[]): Observable<Application[]> {
    if (organisationRefs.length == 0)
      return of([]);

    return zip(
      organisationRefs.map(o =>
        this.orgAdminService.getOrganisation(o)
          .pipe(
            map(org => org.applications ?? []),
            catchError(err => {
              return of([]); // return empty list in case of the error
            })

          ))
    ).pipe(
      map(apps => {
        const flatList = apps.flat();
        const appMap = new Map(flatList.map(item => [item.id, item]));
        return [...appMap.values()].sort(this.compareApplicationsByTranslation);
    }));
  }

  private compareApplicationsByTranslation = (appA: Application, appB: Application): number => {
    const translationKeyPrefix = 'apps.';
    let translationKeyA = translationKeyPrefix + appA.identifier;
    let translationKeyB = translationKeyPrefix + appB.identifier;
    let translationA = this.translateService.instant(translationKeyA) as string;
    let translationB = this.translateService.instant(translationKeyB) as string;

    return translationA.localeCompare(translationB);
  }

  private processError = ( networkError: any ) => {
    this.error = true;
    if (networkError == null)
      return;

    let graphQlErrors = networkError.error?.errors as GraphQLError[] ?? [];
    if (graphQlErrors.length > 0 && graphQlErrors[0].extensions['code']) {
      this.errorCode = graphQlErrors[0].extensions['code'] as string;
    }
  }
}
